import { useEffect, useState } from 'react';
import { apiUrl } from '../../../api/apiUrl';
import http from '../../../api/axios';
import DataTable from '../../../components/table/DataTable';
import { PlusIcon } from '@heroicons/react/24/outline';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Button from '../../../components/button/Button';
import { toast } from 'react-toastify';
import { useCurrentCompany } from '../../../hooks/useCompany';
import { paths } from '../../RouterContainer';
import { columns } from '../../../components/columns/Company/CodeBookItemPriceColumns';
import { useTranslation } from '../../../context/translationContext';
import { VatCode } from '../../../types/VatCode';
import { DirectionDoc } from '../../../types/Invoice';
import { Loader } from '../../../components/loader/Loader';
import { CodeBookItemPrice } from '../../../types/CodeBookItemPrice';

/**
 * Page component represents code book items for current company
 */
export const CodeBookItemsPricePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState<CodeBookItemPrice[] | null>(null);
  const [vatCodes, setVatCodes] = useState<VatCode[] | undefined>();
  const { id } = useParams();
  const { company } = useCurrentCompany();

  /**
   * Loads company code book items
   */
  const fetchData = async () => {
    if (!company) return;
    const response = await http.apiGet({
      url: `${apiUrl.CODE_BOOK_ITEM_PRICE_LIST}?parentId=${id}`,
    });
    setData(response?.data);
  };

  /**
   * Loads vat codes
   */
  const fetchVatCodes = async () => {
    const response = await http.apiGet({
      url: `${apiUrl.VAT_CODE_GET_LIST_FILTERED}?direction=${DirectionDoc.OUTPUT_DOC}`,
    });
    if (response?.data) {
      setVatCodes(response?.data);
    }
  };

  useEffect(() => {
    fetchData();
    fetchVatCodes();
  }, [company]);
  /**
   * Removes code book item
   * @param id
   * @returns
   */
  const onRemoveItem = async (id: string | number) => {
    if (id === null) return;
    const response = await http.apiDelete({
      url: `${apiUrl.CODE_BOOK_ITEM_PRICE_DELETE}?id=${id}`,
    });
    if (response?.status === 204) {
      toast.success('Success!');
      fetchData();
    }
  };

  /**
   * Redirects to company edit page
   * @param id of company
   */
  const onEditItem = (priceId: string | number) => {
    if (priceId === null) return;
    navigate(
      `/${paths.CODE_BOOK_ITEMS}/${Number(id)}/${paths.CODE_BOOK_ITEMS_PRICE}/edit/${priceId}`,
    );
  };

  if (!vatCodes) return <Loader />;
  return (
    <>
      <div className="flex justify-between">
        <h1 className="text-3xl font-bold mb-5">{t('page.code-book-item-price.headline')}</h1>
        <Link to={`/${paths.CODE_BOOK_ITEMS}/${id}/${paths.CODE_BOOK_ITEMS_PRICE}/create`}>
          <Button
            title={t('general.create-new')}
            color="green"
            icon={<PlusIcon className="w-4 h-4 mr-2" />}
          />
        </Link>
      </div>
      <DataTable
        data={data}
        columns={columns()}
        handleRemove={onRemoveItem}
        handleEdit={onEditItem}
      />
    </>
  );
};
